import { template } from 'underscore';
import $ from '../../../../FrontendBundle/Resources/js/jquery';
import { EventEmitter, Logger } from '../../../../FrontendBundle/Resources/js/ofcs';
import ContainerItem from './item/containerItem';

/**
 * @extends EventEmitter
 */
export default class LayoutFactory extends EventEmitter {
    /**
     * Fired before layout data is loaded.
     *
     * @event beforeLoadData
     * @param {LayoutFactory} me Layout factory
     * @param {Number} layoutId Layout ID
     */

    /**
     * Fired after layout data has been successfully loaded.
     *
     * @event loadData
     * @param {LayoutFactory} me Layout factory
     * @param {Number} layoutId Layout ID
     * @param {jqXHR} jqXhr jQuery
     * @param {String} textStatus Response text status
     */

    /**
     * Fired before the canvas is created.
     *
     * @event beforeCreateCanvas
     * @param {Pimple} container Dependency injection container
     * @param {Object} data Data
     * @param {Editor} editor Editor
     */

    /**
     * Fired after the canvas has been created.
     *
     * @event createCanvas
     * @param {Pimple} container Dependency injection container
     * @param {SvgCanvas} canvas Canvas
     * @param {Object} data Data
     * @param {Editor} editor Editor
     */

    /**
     * Creates new instance of canvas. Needs a parent container instance to
     * forward parameters into the canvas specific dependency injection
     * containers.
     *
     * @constructor
     * @param {Pimple} container Pimple container to get parameters
     */
    constructor(container) {
        super();
        this.loadTemplate = template(container.get('editor.url.loadUrl'));
        this.container = container;
    }

    /**
     * Return container
     *
     * @return {Pimple}
     */
    getContainer() {
        return this.container;
    }

    /**
     * @param {number} layoutId
     * @param {Object} wrapper
     * @param {Object} data
     * @param {Editor} editor
     * @return {Promise}
     */
    factory(layoutId, wrapper, data, editor) {
        Logger.log('layoutFactory.factory()', layoutId, wrapper, data, editor);

        if (data) {
            return this.createCanvas(this.container, data, editor);
        }

        this.trigger('beforeLoadData', this, layoutId);

        return new Promise((resolve, reject) => {
            $.ajax({
                url: this.loadTemplate({ id: layoutId }),
                cache: false,
                type: 'GET',
                context: this,
                hideButtons: true,
                success: (response) => {
                    this.createCanvas(this.container, response, editor)
                        .then(resolve)
                        .catch(reject);
                },
                complete: (jqXhr, textStatus) => {
                    this.trigger('loadData', this, layoutId, jqXhr, textStatus);
                },
            });
        });
    }

    /**
     * @param {Pimple} container
     * @param {Object} data
     * @param {Editor} editor
     */
    async createCanvas(container, data, editor) {
        Logger.log('layoutFactory.createCanvas()', data);

        this.trigger('beforeCreateCanvas', container, data, editor);

        const canvas = container.get('canvas');
        const wrapper = container.get('canvas.svg.element');
        const itemViewManager = container.get('canvas.itemViewManager');
        const itemEnvironmentFactory = container.get('editor.itemEnvironmentFactory');
        const itemFactory = container.get('canvas.itemFactory');
        const layerManager = container.get('canvas.svg.layerManager');
        const projectListItemStore = itemFactory.getProjectListItemStore();
        const productionDataStore = itemFactory.getProductionDataStore();
        let rootItem;

        layerManager.createUiLayer('select');
        layerManager.createUiLayer('handles');

        // Important! Calls for side-effects :(
        container.get('canvas.contextVisualizer');
        container.get('canvas.validator.view');

        wrapper.attr('tabindex', -1);

        projectListItemStore.setProjectListItems(data.projectListItems);
        productionDataStore.addProductFamilies(data.productFamilies);

        itemFactory.on('create', (item) => {
            if (data.productionDatas) productionDataStore.addProductionData(item, data.productionDatas);

            const env = itemEnvironmentFactory.createEnvironment(item);

            if (env) {
                env.init(data);

                if (item.hasParentItem()) {
                    item.getParentItem().getItems().on('add', addedItem => addedItem === item && env.activate());
                    item.getParentItem().getItems().on('remove', addedItem => addedItem === item && env.deactivate());
                } else {
                    env.activate();
                }
            }

            if (!rootItem) {
                editor.setRootItem(item);

                if (projectListItemStore.hasProjectListItem(item.get('itemId'))) {
                    const projectListItem = projectListItemStore.getProjectListItem(item.get('itemId'));
                    editor.setNote(projectListItem.note);
                }

                rootItem = item;
                itemViewManager.setRootItem(item);
            }
        });

        const items = await this._importItemData(data.figures, null);

        // create guide manager view, if guide manager is present
        const guideManager = container.get('canvas.guideManager');
        if (guideManager) {
            container.get('canvas.svg.guideManagerView');

            if (items.length >= container.get('canvas.guideManagerThreshold')) {
                guideManager.disable();

                const message = container.get('editor.translations').warning.guidesDisabled;
                $('#alert-messages').alertMessages('add', 'warn', message);
            }
        }

        // create grid view
        container.get('canvas.gridManager');

        this.trigger('createCanvas', container, canvas, data, editor);

        canvas.overlayManager = container.get('canvas.overlayManager');
        canvas.initializeInteraction();

        return canvas;
    }

    /**
     * @param {Object} itemDataParam
     * @param {AbstractItem} parentItem
     * @return {Promise}
     * @private
     */
    async _importItemData(itemDataParam, parentItem) {
        const itemFactory = this.container.get('canvas.itemFactory');
        const contextManager = this.container.get('canvas.contextManager');
        const configBuilder = this.container.get('canvas.item.configBuilder');

        const itemData = configBuilder.build(itemDataParam, contextManager.findNearestContextByItem(parentItem));

        const item = await itemFactory.create(itemData.type, itemData, parentItem);

        if (parentItem) {
            parentItem.getItems().add(item);
        }

        if (item instanceof ContainerItem && itemData.children) {
            const childPromises = itemData.children
                .map(childItemData => this._importItemData(childItemData, item));

            const items = await Promise.all(childPromises);

            return items.reduce((flattened, childItems) => flattened.concat(childItems), []);
        }

        return [item];
    }
}
